/**
 * Reports → Managers keeping up (Reports step 3, DESIGN-reports.md; Rob's decisions 12/10/2026).
 *
 * How long approvals take, who is keeping up, and what is stuck right now.
 *
 *  - "Stuck" is the reminder ladder he already has (1a): waiting past the first reminder,
 *    or escalated to the admins. The thresholds come from the server, so the words here
 *    move if a superadmin moves the ladder.
 *  - "Typical" is the middle value (2a), with the clock starting when a request REACHED
 *    that approver — the reminders' own clock.
 *  - Who sees what is decided on the server (3): the requests of people in your reach, and
 *    every approver of those requests.
 *  - Each waiting request opens to say why it went where it went and who can act on it now
 *    (4a — the routing view from FEATURES-OUTSTANDING C7).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { reportsApi, apiErrorMessage, saveBlobResponse } from '../../lib/api';
import { formatDateGB } from '../../lib/dates';
import { formatDuration, formatWaiting, stepLabel } from '../../lib/approvalTimes';
import {
  ReportFrame, Card, Chip, PillSelect, PeriodPicker, DownloadMenu, PrintButton, NoAccess, InfoTip, HEX,
  ScrollTable,
} from '../../components/reports/ReportUi';

const PAGE_DOWNLOADS = [
  { value: 'xlsx', label: 'Excel (all three tables)' },
  { value: 'csv_waiting', label: 'CSV: waiting now' },
  { value: 'csv_approvers', label: 'CSV: by approver' },
  { value: 'csv_decisions', label: 'CSV: every decision' },
];

const short = (iso) => (iso ? formatDateGB(iso).slice(0, 5) : '');
const firstName = (name) => (name || '').split(' ')[0];

const Stat = ({ label, value, sub, navy = false, active, onClick, testid, tip }) => (
  <div role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
       onClick={onClick} data-testid={testid} className={`text-left ${onClick ? 'cursor-pointer' : ''}`}>
    <Card className={`h-full transition-shadow ${onClick ? 'hover:shadow-[0_10px_30px_rgba(19,31,60,.10)]' : ''} ${
      navy ? 'relative overflow-hidden !bg-hive-navy !border-hive-navy text-white dark:!bg-hive-navy' : ''} ${
      active ? 'ring-2 ring-hive-orange' : ''}`}>
      {navy && <div className="absolute -right-5 -top-4 w-24 h-24 bg-hive-orange opacity-[.12]" style={{ clipPath: HEX }} />}
      <p className={`text-[12.5px] font-bold ${navy ? 'text-hive-muted-cool' : 'text-hive-muted dark:text-muted-foreground'}`}>
        {label}{tip}
      </p>
      <p className="font-poppins text-[28px] font-bold leading-tight">{value}</p>
      {sub && <p className={`text-xs ${navy ? 'text-hive-muted-cool' : 'text-hive-muted dark:text-muted-foreground'}`}>{sub}</p>}
    </Card>
  </div>
);

const TypicalTip = ({ th }) => (
  <InfoTip label="About typical time">
    The <b>middle</b> time taken, so one request left over a holiday doesn't skew it. The clock starts
    when a request <b>reaches that approver</b> — for a second step, when the first step was signed
    off — and weekends count. It's the same clock the reminders use: the first goes after{' '}
    {th?.stuck_after || '2 days'}, and it goes to the admins after {th?.escalate_after || '7 days'}.
  </InfoTip>
);

/** Status chips for one waiting request. */
const StatusChips = ({ w, th }) => (
  <>
    {w.escalated_on && <Chip tone="danger">Escalated</Chip>}
    {!w.escalated_on && w.stuck && w.reminders > 0 && <Chip tone="pending">Reminded ×{w.reminders}</Chip>}
    {!w.escalated_on && w.stuck && !w.reminders && w.step && <Chip tone="pending">Over {th?.stuck_after}</Chip>}
    {!w.step && <Chip tone="danger">No approval steps</Chip>}
    {w.nobody_can_act && <Chip tone="danger">Nobody can approve</Chip>}
    {w.dates_passed && <Chip tone="danger">Dates passed</Chip>}
  </>
);

/** What the reminder ladder has done, and will do next, in one or two sentences. */
const reminderSentence = (w, d) => {
  if (!w.step) return "The reminders can't see it, because it has no approval steps.";
  if (!d.ladder_on) return 'Reminders are switched off, so nobody is being chased.';
  const who = w.waiting_on.kind === 'group' ? 'the group' : firstName(w.waiting_on.name);
  const parts = [];
  if (d.reminders_sent) {
    parts.push(`${d.reminders_sent} sent to ${who}${d.last_reminder_on ? `, the last on ${formatDateGB(d.last_reminder_on)}` : ''}.`);
  } else {
    parts.push(`None sent yet${d.next_reminder_on ? ` — the first goes on ${formatDateGB(d.next_reminder_on)}` : ''}.`);
  }
  if (d.escalated_on) parts.push(`Escalated to the admins on ${formatDateGB(d.escalated_on)}.`);
  else {
    if (d.reminders_sent && d.next_reminder_on) parts.push(`The next goes on ${formatDateGB(d.next_reminder_on)}.`);
    if (d.escalates_on) parts.push(`If it's still waiting, it goes to the admins on ${formatDateGB(d.escalates_on)}.`);
  }
  return parts.join(' ');
};

const WaitingDetail = ({ w }) => {
  const d = w.detail || {};
  const admins = d.admins || [];
  return (
    <div className="rounded-2xl border border-hive-line dark:border-border bg-hive-tile dark:bg-card px-4 py-3 text-[13.5px] leading-relaxed"
         data-testid="mku-waiting-detail">
      <p>
        <b>Why it went {w.waiting_on.kind === 'nobody' ? 'nowhere'
          : w.waiting_on.kind === 'group' ? `to the ${w.waiting_on.name} group` : `to ${w.waiting_on.name}`}:</b> {d.why}
      </p>
      {d.profile_note && <p className="text-hive-pending-fg">{d.profile_note}</p>}
      {w.step && (
        <p>
          <b>Who can act on it now:</b>{' '}
          {(d.can_act || []).length
            ? (d.can_act || []).map((n) => `${n} ✓`).join(' · ')
            : <span className="text-hive-danger-fg font-bold">nobody in this step</span>}
          {admins.length > 0 && <> · and any admin: {admins.slice(0, 3).join(', ')}{admins.length > 3 ? ` and ${admins.length - 3} more` : ''}</>}
        </p>
      )}
      {!w.step && admins.length > 0 && <p><b>Who can act on it now:</b> any admin: {admins.join(', ')}</p>}
      {(d.cannot_act || []).length > 0 && (
        <p className="text-hive-muted dark:text-muted-foreground">
          Can't act on it: {d.cannot_act.map((c) => `${c.name} (${c.why_not})`).join(' · ')}
        </p>
      )}
      <p><b>Reminders:</b> {reminderSentence(w, d)}</p>
      {d.nudged_on && <p>{firstName(w.name)} chased it themselves on {formatDateGB(d.nudged_on)}.</p>}
      {(d.problems || []).map((p) => <p key={p} className="text-hive-danger-fg font-bold">{p}</p>)}
      {d.viewer_can_act && (
        <Link to={`/approvals?request=${w.request_id}`} className="text-hive-orange font-extrabold hover:underline print:hidden">
          Open it in Approvals →
        </Link>
      )}
    </div>
  );
};

const ManagersKeepingUpPage = () => {
  const [period, setPeriod] = useState({ year: null, start: null, end: null });
  const [filters, setFilters] = useState({ department_id: '', location_id: '' });
  const [onlyStuck, setOnlyStuck] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [openId, setOpenId] = useState(null);          // a waiting request
  const [approver, setApprover] = useState(null);      // an approver row
  const [approverData, setApproverData] = useState(null);
  const [busy, setBusy] = useState(null);

  const baseParams = useMemo(() => {
    const p = period.start && period.end ? { start: period.start, end: period.end }
      : (period.year ? { year: period.year } : {});
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [period, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsApi.keepingUp(baseParams);
      setData(res.data);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      else toast.error(apiErrorMessage(err, 'Could not load the report'));
    } finally {
      setLoading(false);
    }
  }, [baseParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setApprover(null); setApproverData(null); }, [baseParams]);

  useEffect(() => {
    if (!approver) return undefined;
    let cancelled = false;
    setApproverData(null);
    (async () => {
      try {
        const res = await reportsApi.keepingUpApprover({ ...baseParams, key: approver.key });
        if (!cancelled) setApproverData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(apiErrorMessage(err, 'Could not load their decisions'));
      }
    })();
    return () => { cancelled = true; };
  }, [approver, baseParams]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await reportsApi.exportKeepingUp({ ...baseParams, format: fmt });
      const name = saveBlobResponse(res, `managers-keeping-up.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`);
      toast.success(`Downloaded ${name}`);
    } catch (err) {
      let message = 'Could not build the download';
      try {
        const text = await err?.response?.data?.text?.();
        if (text) message = JSON.parse(text).detail || message;
      } catch { /* keep the fallback */ }
      toast.error(message);
    } finally {
      setBusy(null);
    }
  };

  if (forbidden) return <ReportFrame><NoAccess /></ReportFrame>;

  const h = data?.headline;
  const th = data?.thresholds;
  const waiting = (data?.waiting || []).filter((w) => !onlyStuck || w.stuck);
  const approvers = data?.approvers || [];
  const maxTypical = Math.max(1, ...approvers.map((a) => a.typical_hours || 0));
  // "this leave year" only when it IS this leave year; "in leave year 2025" looking back.
  const periodWords = !data ? '' : data.period.kind === 'custom' ? 'in these dates'
    : data.period.is_current === false ? `in ${data.period.label.charAt(0).toLowerCase()}${data.period.label.slice(1)}`
      : 'this leave year';

  return (
    <ReportFrame>
      <p className="text-[13px] text-hive-muted dark:text-muted-foreground mb-1 print:hidden">
        <Link to="/reports" className="hover:underline">Reports</Link> › <b className="text-hive-navy dark:text-foreground">Managers keeping up</b>
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-poppins text-[26px] font-semibold">Managers keeping up</h1>
          <p className="text-sm text-hive-muted dark:text-muted-foreground">
            {data ? `${data.scope.label} · ${data.period.label}${data.period.range ? ` (${data.period.range})` : ''}` : 'Loading…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data && (
            <PeriodPicker
              period={data.period}
              onYear={(y) => setPeriod({ year: y, start: null, end: null })}
              onDates={(s, e) => setPeriod({ year: null, start: s, end: e })}
            />
          )}
          <PrintButton />
          <DownloadMenu options={PAGE_DOWNLOADS} busy={!!busy} onPick={download} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3 print:hidden">
        <PillSelect label="Department" value={filters.department_id} onChange={(v) => setFilters((f) => ({ ...f, department_id: v }))}>
          <option value="">All departments</option>
          {(data?.filters.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </PillSelect>
        <PillSelect label="Location" value={filters.location_id} onChange={(v) => setFilters((f) => ({ ...f, location_id: v }))}>
          <option value="">All locations</option>
          {(data?.filters.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </PillSelect>
      </div>

      <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3.5 my-4 ${loading ? 'opacity-60' : ''}`}>
        <Stat navy testid="mku-stat-waiting" active={!onlyStuck} onClick={() => setOnlyStuck(false)}
              label="Waiting now"
              value={h ? `${h.waiting} ${h.waiting === 1 ? 'request' : 'requests'}` : '–'}
              sub={!h ? '' : h.waiting ? `the oldest has waited ${formatWaiting(h.oldest_hours)}` : 'nothing is waiting for a decision'} />
        <Stat testid="mku-stat-stuck" active={onlyStuck} onClick={() => setOnlyStuck((s) => !s)}
              label="Stuck" value={h ? h.stuck : '–'}
              sub={!h ? '' : !h.stuck ? `nothing has waited more than ${th.stuck_after}`
                : [`waiting more than ${th.stuck_after}`,
                   h.stuck_escalated ? `${h.stuck_escalated === h.stuck && h.stuck > 1 ? `all ${h.stuck}` : h.stuck_escalated} escalated to the admins` : null,
                   h.stuck_dates_passed ? `${h.stuck_dates_passed} for dates that have already passed` : null,
                ].filter(Boolean).join(' · ')} />
        <Stat testid="mku-stat-typical" label="Typical time to a decision" tip={<> <TypicalTip th={th} /></>}
              value={h ? formatDuration(h.typical_hours) : '–'}
              sub={!h ? '' : h.decisions ? `across ${h.decisions} ${h.decisions === 1 ? 'decision' : 'decisions'} ${periodWords}` : `no decisions ${periodWords}${periodWords === 'this leave year' ? ' yet' : ''}`} />
        <Stat testid="mku-stat-within" label={`Decided within ${th?.stuck_after || '2 days'}`}
              value={h && h.within_pct !== null && h.within_pct !== undefined ? `${h.within_pct}%` : '–'}
              sub={th ? `the first reminder goes after ${th.stuck_after}` : ''} />
      </div>

      {th && !th.ladder_on && (
        <p className="mb-3 rounded-xl bg-hive-pending-bg text-hive-pending-fg px-4 py-2 text-[13px] font-bold">
          Approval reminders are switched off at the moment, so nothing is being chased or escalated.
          "Stuck" still means waiting more than {th.stuck_after}.
        </p>
      )}

      <Card className={loading ? 'opacity-60' : ''}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-poppins font-semibold text-[17px]">{onlyStuck ? 'Stuck, oldest first' : 'Waiting now, oldest first'}</p>
          <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground">Click one to see who can act on it, and why it went to them</p>
        </div>
        <ScrollTable>
          <table className="w-full text-[13.5px] mt-2" data-testid="mku-waiting">
            <thead>
              <tr className="text-[12px] text-hive-muted dark:text-muted-foreground border-b border-hive-line dark:border-border">
                <th className="text-left font-bold py-2 pr-2">Person</th>
                <th className="text-left font-bold px-2">Leave</th>
                <th className="text-left font-bold px-2">Waiting on</th>
                <th className="text-left font-bold px-2">Step</th>
                <th className="text-right font-bold px-2">Waiting</th>
                <th className="text-left font-bold pl-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {waiting.map((w) => (
                <React.Fragment key={w.request_id}>
                  <tr onClick={() => setOpenId((id) => (id === w.request_id ? null : w.request_id))}
                      className={`border-b border-hive-line-soft dark:border-border cursor-pointer align-top hover:bg-hive-tile dark:hover:bg-muted ${
                        openId === w.request_id ? 'bg-hive-pending-bg dark:bg-muted' : ''}`}>
                    <td className="py-2 pr-2">
                      <b>{w.name}</b>
                      <span className="block text-[12px] text-hive-muted dark:text-muted-foreground">{w.department || 'No department'}</span>
                    </td>
                    <td className="py-2 px-2">
                      {w.leave_type}
                      <span className="block text-[12px] text-hive-muted dark:text-muted-foreground">{w.when}</span>
                    </td>
                    <td className="py-2 px-2">
                      {w.waiting_on.kind === 'nobody' ? <span className="text-hive-danger-fg font-bold">Nobody set</span> : w.waiting_on.name}
                      <span className="block text-[12px] text-hive-muted dark:text-muted-foreground">
                        {w.escalated_on ? `escalated to the admins on ${short(w.escalated_on)}`
                          : w.waiting_on.kind === 'group' ? `${w.waiting_on.sub} · any one of them`
                            : w.waiting_on.kind === 'nobody' ? 'only an admin can act' : ''}
                      </span>
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap">{stepLabel(w.step)}</td>
                    <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">{formatWaiting(w.hours)}</td>
                    <td className="py-2 pl-2"><StatusChips w={w} th={th} /></td>
                  </tr>
                  {openId === w.request_id && (
                    <tr><td colSpan={6} className="pb-3 pt-1"><WaitingDetail w={w} /></td></tr>
                  )}
                </React.Fragment>
              ))}
              {!waiting.length && (
                <tr><td colSpan={6} className="py-6 text-center text-hive-muted">
                  {onlyStuck ? 'Nothing is stuck.' : 'Nothing is waiting for a decision.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </ScrollTable>
        <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground mt-3">
          "Stuck" uses the reminders you already have: waiting more than {th?.stuck_after || '2 days'}, or escalated to
          the admins after {th?.escalate_after || '7 days'}. "Dates passed" means the leave has started or finished while
          still waiting for a decision — those are the ones to chase first.
        </p>
      </Card>

      <Card className={`mt-3.5 ${loading ? 'opacity-60' : ''}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-poppins font-semibold text-[17px]">How long approvals take, by approver</p>
          <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground">Decisions made {periodWords} · slowest first · click an approver to see each request</p>
        </div>
        <ScrollTable>
          <table className="w-full text-[13.5px] mt-2" data-testid="mku-approvers">
            <thead>
              <tr className="text-[12px] text-hive-muted dark:text-muted-foreground border-b border-hive-line dark:border-border">
                <th className="text-left font-bold py-2 pr-2">Approver</th>
                <th className="text-right font-bold px-2">Decisions</th>
                <th className="text-right font-bold px-2 whitespace-nowrap">Typical time <TypicalTip th={th} /></th>
                <th className="px-2 w-[130px]" aria-label="Typical time as a bar" />
                <th className="text-right font-bold px-2">Slowest</th>
                <th className="text-right font-bold px-2 whitespace-nowrap">Within {th?.stuck_after || '2 days'}</th>
                <th className="text-right font-bold px-2 whitespace-nowrap">Waiting now</th>
                <th className="text-right font-bold pl-2">Escalated</th>
              </tr>
            </thead>
            <tbody>
              {approvers.map((a) => {
                const slow = (a.typical_hours || 0) > (th?.stuck_after_hours || 48);
                return (
                  <tr key={a.key} onClick={() => setApprover((cur) => (cur?.key === a.key ? null : a))}
                      className={`border-b border-hive-line-soft dark:border-border cursor-pointer hover:bg-hive-tile dark:hover:bg-muted ${
                        approver?.key === a.key ? 'bg-hive-pending-bg dark:bg-muted' : ''}`}>
                    <td className="py-2 pr-2">
                      <b>{a.name}</b>
                      <span className="block text-[12px] text-hive-muted dark:text-muted-foreground">{a.sub}</span>
                    </td>
                    <td className="text-right px-2 tabular-nums">{a.decisions}</td>
                    <td className={`text-right px-2 tabular-nums whitespace-nowrap ${slow ? 'text-hive-danger-fg font-bold' : ''}`}>{formatDuration(a.typical_hours)}</td>
                    <td className="px-2">
                      {a.typical_hours !== null && a.typical_hours !== undefined && (
                        <span className="inline-block h-2 rounded-full align-middle"
                              style={{ width: `${Math.max(6, (120 * a.typical_hours) / maxTypical)}px`,
                                       background: slow ? '#E0655F' : '#F0A32A' }} />
                      )}
                    </td>
                    <td className="text-right px-2 tabular-nums whitespace-nowrap">{formatDuration(a.slowest_hours)}</td>
                    <td className="text-right px-2 tabular-nums">{a.within_pct === null || a.within_pct === undefined ? '–' : `${a.within_pct}%`}</td>
                    <td className="text-right px-2 tabular-nums">
                      {a.stuck_now ? <Chip tone="danger">{a.waiting_now}</Chip> : a.waiting_now}
                    </td>
                    <td className="text-right pl-2 tabular-nums">
                      {a.escalated ? <Chip tone="danger">{a.escalated}</Chip> : 0}
                    </td>
                  </tr>
                );
              })}
              {!approvers.length && (
                <tr><td colSpan={8} className="py-6 text-center text-hive-muted">No approvals {periodWords}.</td></tr>
              )}
            </tbody>
          </table>
        </ScrollTable>

        {approver && (
          <div className="mt-3.5 rounded-2xl border border-hive-line dark:border-border bg-hive-tile dark:bg-card p-4" data-testid="mku-approver-drill">
            <div className="flex items-start justify-between gap-2">
              <p className="font-bold">
                {approver.name} · {approver.decisions} {approver.decisions === 1 ? 'decision' : 'decisions'} {periodWords}, slowest first
              </p>
              <button onClick={() => setApprover(null)} aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            {!approverData ? <p className="text-sm text-hive-muted mt-2">Loading…</p> : (
              <table className="w-full text-[13px] mt-2">
                <thead>
                  <tr className="text-[12px] text-hive-muted border-b border-hive-line dark:border-border">
                    <th className="text-left font-bold py-1.5">Person</th>
                    <th className="text-left font-bold">Leave</th>
                    <th className="text-left font-bold">Reached them</th>
                    <th className="text-left font-bold">Decided</th>
                    <th className="text-right font-bold">Time taken</th>
                    <th className="text-left font-bold pl-3">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {approverData.decisions.map((d) => (
                    <tr key={`${d.request_id}-${d.step.n}`} className="border-b border-hive-line-soft dark:border-border align-top">
                      <td className="py-1.5">{d.name}</td>
                      <td>
                        {d.leave_type}
                        <span className="block text-[11.5px] text-hive-muted">{d.when}{d.step.of > 1 ? ` · step ${d.step.n} of ${d.step.of}` : ''}</span>
                      </td>
                      <td>{formatDateGB(d.reached_on)}</td>
                      <td>{formatDateGB(d.decided_on)}</td>
                      <td className="text-right tabular-nums whitespace-nowrap">{formatDuration(d.hours)}</td>
                      <td className="pl-3">
                        <Chip tone={d.outcome === 'Approved' ? 'success' : 'grey'}>{d.outcome}</Chip>
                        {(d.decided_by || d.escalated) && (
                          <span className="block text-[11.5px] text-hive-muted">
                            {d.escalated ? 'escalated, then ' : ''}{d.decided_by ? `decided by ${d.decided_by}` : 'decided'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!approverData.decisions.length && (
                    <tr><td colSpan={6} className="py-3 text-hive-muted">No decisions {periodWords} — only what's waiting now.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground mt-3">
          <b>Typical time</b> is the middle value, so one request left over a holiday doesn't skew it. The clock starts when a
          request <b>reaches that approver</b> — the same clock the reminders use — and weekends count. If an admin steps in
          after an escalation, the time still counts against the person it was waiting on. A group's requests count against
          the group, whoever answered. Requests approved the moment they were booked (by the approver themselves) aren't counted.
        </p>
      </Card>
    </ReportFrame>
  );
};

export default ManagersKeepingUpPage;
